import type { HeadlinePinPosition } from '../../types/campaign';
import { CopyAllButton, VariationCard } from './variationsCardPrimitives';

// P2b-3 Build View commit 4: inline variant of <VariationsListPopover>.
// Same per-card layout + Copy-all behaviour, rendered directly inside a
// Build View content section without the popover positioning shell.
//
// Build View parent uses <BuildViewSection title="Body Copy" badge={...}>
// for the section header. This component returns just the cards — the
// section's `badge` slot carries the Copy-all button (composed by the
// content section component).
//
// Empty arrays render the "No variations authored" muted-italic state
// inline; the parent doesn't need to gate.
//
// Pinned Headlines spec §2.3: optional `pins` prop runs parallel to
// `variations`. Only the Google headlines section passes it — other
// content sections (body copy / descriptions / Meta headlines) omit
// it and the cards render without a pin badge.

type Props = {
  variations: string[] | null | undefined;
  pins?: Array<HeadlinePinPosition> | null;
};

export function VariationsListInline({ variations, pins }: Props) {
  const raw = variations ?? [];
  const filtered: Array<{ value: string; pin: HeadlinePinPosition }> = [];
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i];
    if (typeof v !== 'string' || v.length === 0) continue;
    filtered.push({ value: v, pin: pins ? pins[i] ?? null : null });
  }

  if (filtered.length === 0) {
    return (
      <p className="py-2 text-sm text-slate-400 italic">No variations authored</p>
    );
  }

  return (
    <ul className="space-y-3">
      {filtered.map((entry, idx) => (
        <VariationCard
          key={idx}
          index={idx + 1}
          value={entry.value}
          pinPosition={entry.pin}
        />
      ))}
    </ul>
  );
}

// Convenience re-export so content sections can use the same Copy-all
// button in the section's `badge` slot. Keeps the per-section header
// style consistent across Build View.
export { CopyAllButton } from './variationsCardPrimitives';
